/**
 * Helper client — flags búsqueda semántica + Fase 2 integration (espejo server).
 * Destinos: listas pipe `semantic.search.integration.principal|secondary.{sourceModule}`
 */

const MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
  'assistance',
  'financial',
  'vision',
  'regulation',
];

export const PRINCIPAL_MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
];

export const SECONDARY_MODULE_KEYS = [
  'assistance',
  'financial',
  'vision',
  'regulation',
];

const MODULE_STATE_KEY = {
  technology: 'semanticSearchTechnologyEnabled',
  alternative: 'semanticSearchAlternativeEnabled',
  experience: 'semanticSearchExperienceEnabled',
  innovation: 'semanticSearchInnovationEnabled',
  assistance: 'semanticSearchAssistanceEnabled',
  financial: 'semanticSearchFinancialEnabled',
  vision: 'semanticSearchVisionEnabled',
  regulation: 'semanticSearchRegulationEnabled',
};

/** Redux key por módulo origen → lista pipe de destinos */
const INTEGRATION_TARGET_LIST_STATE_KEY = {
  technology: 'semanticSearchIntegrationPrincipalTechnology',
  alternative: 'semanticSearchIntegrationPrincipalAlternative',
  experience: 'semanticSearchIntegrationPrincipalExperience',
  innovation: 'semanticSearchIntegrationPrincipalInnovation',
  financial: 'semanticSearchIntegrationSecondaryFinancial',
  assistance: 'semanticSearchIntegrationSecondaryAssistance',
  regulation: 'semanticSearchIntegrationSecondaryRegulation',
  vision: 'semanticSearchIntegrationSecondaryVision',
};

const DEFAULT_TARGET_BY_SOURCE = {
  technology: 'financial|assistance|regulation|vision|experience',
  alternative: 'financial|assistance|regulation|vision|experience',
  innovation: 'financial|assistance|regulation|vision|experience',
  experience: 'financial|assistance|regulation|vision|technology|alternative|innovation',
  financial: 'technology|alternative|experience|innovation',
  assistance: 'technology|alternative|experience|innovation',
  regulation: 'technology|alternative|experience|innovation',
  vision: 'technology|alternative|experience|innovation',
};

export const isConfigTruthy = (value) => {
  if (value === true) return true;
  if (value === false || value === undefined || value === null) return false;
  return String(value).toLowerCase() === 'true';
};

/** Master + módulo: additional.support.*.enabled */
export const isAdditionalSupportEnabled = (configurationState, moduleKey) => {
  if (!isConfigTruthy(configurationState?.additionalSupportEnabled)) return false;
  if (!moduleKey) return false;
  const camel = `additionalSupport${moduleKey.charAt(0).toUpperCase()}${moduleKey.slice(1)}Enabled`;
  return isConfigTruthy(configurationState?.[camel]);
};

/** Master + módulo: support.popup.*.enabled */
export const isSupportPopupEnabled = (configurationState, moduleKey) => {
  if (!isConfigTruthy(configurationState?.supportPopupEnabled)) return false;
  if (!moduleKey) return false;
  const camel = `supportPopup${moduleKey.charAt(0).toUpperCase()}${moduleKey.slice(1)}Enabled`;
  return isConfigTruthy(configurationState?.[camel]);
};

/** support.form.mode: static | hybrid | dynamic (acepta hibrid / dinamic) */
export const getSupportFormMode = (configurationState) => {
  const raw = String(configurationState?.supportFormMode || 'hybrid').trim().toLowerCase();
  if (raw === 'static') return 'static';
  if (raw === 'dynamic' || raw === 'dinamic') return 'dynamic';
  return 'hybrid';
};

export const parsePipeList = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (!s) return [];
  return s
    .split('|')
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p && MODULE_KEYS.includes(p));
};

export const isSemanticSearchMasterEnabled = (configurationState) => {
  return isConfigTruthy(configurationState?.semanticSearchEnabled);
};

export const isSemanticSearchModuleEnabled = (configurationState, moduleKey) => {
  if (!isSemanticSearchMasterEnabled(configurationState)) return false;
  const stateKey = MODULE_STATE_KEY[moduleKey];
  if (!stateKey) return false;
  return isConfigTruthy(configurationState[stateKey]);
};

export const isLikeCompatibilityEnabled = (configurationState) => {
  const value = configurationState?.semanticSearchLikeCompatibilityEnabled;
  if (value === undefined || value === null || value === '') return true;
  return isConfigTruthy(value);
};

/** Texto junto a Resultados obtenidos. No cambia el ranking. Default true. */
export const isSemanticSearchModeBadgeEnabled = (configurationState) => {
  const value = configurationState?.semanticSearchModeBadgeEnabled;
  if (value === undefined || value === null || value === '') return true;
  return isConfigTruthy(value);
};

export const isSemanticSearchHelpPopupEnabled = (configurationState) => {
  const value = configurationState?.semanticSearchHelpPopupEnabled;
  if (value === undefined || value === null || value === '') return true;
  return isConfigTruthy(value);
};

/** Leyenda «Buscador Inteligente» bajo el input. Default true. */
export const isSemanticSearchHelpLegendEnabled = (configurationState) => {
  const value = configurationState?.semanticSearchHelpLegendEnabled;
  if (value === undefined || value === null || value === '') return true;
  return isConfigTruthy(value);
};

export const isSemanticBridgeEnabled = (configurationState) => {
  return (
    isSemanticSearchMasterEnabled(configurationState) &&
    isConfigTruthy(configurationState?.semanticSearchBridgeEnabled)
  );
};

export const getSemanticBridgeMode = (configurationState) => {
  return getIntegrationMode(configurationState);
};

export const isIntegrationMasterEnabled = (configurationState) => {
  return isConfigTruthy(configurationState?.semanticSearchIntegrationEnabled);
};

export const isIntegrationPrincipalDirectionEnabled = (configurationState) => {
  return (
    isIntegrationMasterEnabled(configurationState) &&
    isConfigTruthy(configurationState?.semanticSearchIntegrationPrincipalEnabled)
  );
};

export const isIntegrationSecondaryDirectionEnabled = (configurationState) => {
  return (
    isIntegrationMasterEnabled(configurationState) &&
    isConfigTruthy(configurationState?.semanticSearchIntegrationSecondaryEnabled)
  );
};

export const isIntegrationDashboardEnabled = (configurationState) => {
  return (
    isIntegrationMasterEnabled(configurationState) &&
    isConfigTruthy(configurationState?.semanticSearchIntegrationDashboardEnabled)
  );
};

export const isIntegrationViewEnabled = (configurationState) => {
  return (
    isIntegrationMasterEnabled(configurationState) &&
    isConfigTruthy(configurationState?.semanticSearchIntegrationViewEnabled)
  );
};

export const isIntegrationFilterBarEnabled = (configurationState) => {
  const value = configurationState?.semanticSearchIntegrationFilterBarEnabled;
  if (value === undefined || value === null || value === '') return true;
  return isConfigTruthy(value);
};

export const isPanelLexicalFromTableEnabled = (configurationState) => {
  return isConfigTruthy(configurationState?.semanticSearchPanelLexicalFromTableEnabled);
};

export const isPanelComboFromTableEnabled = (configurationState) => {
  return isConfigTruthy(configurationState?.semanticSearchPanelComboFromTableEnabled);
};

export const isM4FromTableEnabled = (configurationState) => {
  return isConfigTruthy(configurationState?.semanticSearchIntegrationM4FromTableEnabled);
};

export const getIntegrationMode = (configurationState) => {
  const mode = String(
    configurationState?.semanticSearchIntegrationMode ||
      configurationState?.semanticSearchBridgeMode ||
      'M3'
  ).toUpperCase();
  if (mode === 'M1' || mode === 'M2' || mode === 'M3' || mode === 'M4') return mode;
  return 'M3';
};

export const getIntegrationDashboardNumber = (configurationState) => {
  const n = Number(configurationState?.semanticSearchIntegrationDashboardNumber);
  if (!Number.isFinite(n) || n < 1) return 9;
  return Math.min(Math.floor(n), 50);
};

export const getSourceGroup = (moduleKey) => {
  if (PRINCIPAL_MODULE_KEYS.includes(moduleKey)) return 'principal';
  if (SECONDARY_MODULE_KEYS.includes(moduleKey)) return 'secondary';
  return null;
};

const getDefaultTargetListRaw = (sourceModuleKey) => DEFAULT_TARGET_BY_SOURCE[sourceModuleKey] || '';

/**
 * Destinos del módulo origen desde lista pipe en Redux.
 * Vacío '' → []; undefined → default cruzado.
 */
export const getIntegrationTargetListForSource = (configurationState, sourceModuleKey) => {
  const stateKey = INTEGRATION_TARGET_LIST_STATE_KEY[sourceModuleKey];
  if (!stateKey) return [];

  const raw = configurationState?.[stateKey];
  if (raw === undefined || raw === null) {
    return (parsePipeList(getDefaultTargetListRaw(sourceModuleKey)) || []).filter(
      (t) => t !== sourceModuleKey
    );
  }
  const parsed = parsePipeList(raw);
  return (parsed || []).filter((t) => t !== sourceModuleKey);
};

export const isIntegrationDestinationEnabled = (
  configurationState,
  sourceModuleKey,
  targetModuleKey
) => {
  if (!MODULE_KEYS.includes(sourceModuleKey) || !MODULE_KEYS.includes(targetModuleKey)) {
    return false;
  }
  if (!isIntegrationMasterEnabled(configurationState)) return false;

  const group = getSourceGroup(sourceModuleKey);
  if (!group) return false;

  if (group === 'principal' && !isIntegrationPrincipalDirectionEnabled(configurationState)) {
    return false;
  }
  if (group === 'secondary' && !isIntegrationSecondaryDirectionEnabled(configurationState)) {
    return false;
  }

  return getIntegrationTargetListForSource(configurationState, sourceModuleKey).includes(
    targetModuleKey
  );
};

export const listIntegrationTargetsFromSource = (configurationState, sourceModuleKey) => {
  if (!MODULE_KEYS.includes(sourceModuleKey)) return [];
  if (!isIntegrationMasterEnabled(configurationState)) return [];

  const group = getSourceGroup(sourceModuleKey);
  if (!group) return [];
  if (group === 'principal' && !isIntegrationPrincipalDirectionEnabled(configurationState)) {
    return [];
  }
  if (group === 'secondary' && !isIntegrationSecondaryDirectionEnabled(configurationState)) {
    return [];
  }

  return getIntegrationTargetListForSource(configurationState, sourceModuleKey);
};

export const isIntegrationModuleEnabled = (configurationState, moduleKey) => {
  if (!MODULE_KEYS.includes(moduleKey)) return false;
  if (!isIntegrationMasterEnabled(configurationState)) return false;
  const group = getSourceGroup(moduleKey);
  if (group === 'principal') return isIntegrationPrincipalDirectionEnabled(configurationState);
  if (group === 'secondary') return isIntegrationSecondaryDirectionEnabled(configurationState);
  return false;
};

export const isIntegrationBridgeAllowed = (
  configurationState,
  sourceModuleKey,
  targetModuleKey
) => isIntegrationDestinationEnabled(configurationState, sourceModuleKey, targetModuleKey);

export const canShowIntegrationViewBridge = (configurationState, sourceModuleKey) => {
  if (!isIntegrationViewEnabled(configurationState)) return false;
  if (!isIntegrationModuleEnabled(configurationState, sourceModuleKey)) return false;
  return listIntegrationTargetsFromSource(configurationState, sourceModuleKey).length > 0;
};

export const canShowIntegrationDashboardBridge = (configurationState, sourceModuleKey) => {
  if (!isIntegrationDashboardEnabled(configurationState)) return false;
  if (!isIntegrationModuleEnabled(configurationState, sourceModuleKey)) return false;
  return listIntegrationTargetsFromSource(configurationState, sourceModuleKey).length > 0;
};

export const SEMANTIC_MODULE_KEYS = MODULE_KEYS;
